// frontend/src/components/reputation/DirectoriesTable.jsx
// Version: 1.0 (growth-agents-stage2)
// Description: Interactive table of all 27 directories across Tier 1, 2, and 3,
//              tracking observations per office, running discrepancy checks,
//              and offering one-click copy packets strictly from canon.

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useUiLanguage, perevesti } from '../../context/UiLanguageContext';
import {
  Building2,
  ExternalLink,
  Copy,
  Check,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  XCircle,
  Layers,
  MapPin,
  X,
} from 'lucide-react';

export const DirectoriesTable = ({ tenant = 'fitenko' }) => {
  const { lang: rawLang, t: contextT } = useUiLanguage();
  const lang = rawLang || 'ru';
  const isEn = lang === 'en';
  const t = useCallback((k, v) => (rawLang ? contextT(k, v) : perevesti('ru', k, v)), [rawLang, contextT]);

  const [directories, setDirectories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tierFilter, setTierFilter] = useState('all'); // 'all' | '1' | '2' | '3'

  // Packet Modal state
  const [activePacketDir, setActivePacketDir] = useState(null);
  const [activeOffice, setActiveOffice] = useState('coral-springs');
  const [packetData, setPacketData] = useState(null);
  const [packetLoading, setPacketLoading] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  // Check state
  const [checkingKey, setCheckingKey] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  const fetchDirectories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/api/v1/reputation/citations/directories?tenant=${tenant}`);
      setDirectories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load directories');
      setDirectories([]);
    } finally {
      setLoading(false);
    }
  }, [tenant]);

  useEffect(() => {
    fetchDirectories();
  }, [fetchDirectories]);

  // Load packet for modal
  const openPacketModal = useCallback(async (dir, officeKey = 'coral-springs') => {
    setActivePacketDir(dir);
    setActiveOffice(officeKey);
    setPacketLoading(true);
    setPacketData(null);
    try {
      const res = await axios.get(
        `/api/v1/reputation/citations/directories/${dir.key}/packet?office_key=${officeKey}&tenant=${tenant}`
      );
      setPacketData(res.data);
    } catch (err) {
      console.error('Failed to load copy packet:', err);
    } finally {
      setPacketLoading(false);
    }
  }, [tenant]);

  const switchOfficeInModal = async (officeKey) => {
    if (!activePacketDir) return;
    setActiveOffice(officeKey);
    setPacketLoading(true);
    try {
      const res = await axios.get(
        `/api/v1/reputation/citations/directories/${activePacketDir.key}/packet?office_key=${officeKey}&tenant=${tenant}`
      );
      setPacketData(res.data);
    } catch (err) {
      console.error('Failed to switch office packet:', err);
    } finally {
      setPacketLoading(false);
    }
  };

  const copyToClipboard = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const copyEntirePacket = () => {
    if (!packetData) return;
    const lines = Object.entries(packetData)
      .map(([k, v]) => `[${k.toUpperCase()}]:\n${v}\n`)
      .join('\n');
    navigator.clipboard.writeText(lines);
    setCopiedKey('ALL');
    setToastMessage(t('rep.directories.toast_packet_copied'));
    setTimeout(() => {
      setCopiedKey(null);
      setToastMessage(null);
    }, 2500);
  };

  const runDiscrepancyCheck = async (dir, officeKey) => {
    const obs = dir.observations?.[officeKey];
    const url = obs?.profile_url;
    if (!url) {
      setToastMessage('URL profile is missing for check');
      setTimeout(() => setToastMessage(null), 3000);
      return;
    }

    setCheckingKey(`${dir.key}-${officeKey}`);
    try {
      const res = await axios.post(
        `/api/v1/reputation/citations/directories/${dir.key}/check?office_key=${officeKey}&public_url=${encodeURIComponent(
          url
        )}&tenant=${tenant}`
      );
      setToastMessage(
        t('rep.directories.toast_check_completed', {
          result: `${res.data.status} — ${res.data.reason}`,
        })
      );
      fetchDirectories();
    } catch (err) {
      setToastMessage(`Error: ${err?.message || 'Check failed'}`);
    } finally {
      setCheckingKey(null);
      setTimeout(() => setToastMessage(null), 4000);
    }
  };

  const list = Array.isArray(directories) ? directories : [];
  const filteredDirectories = list.filter((d) => {
    if (tierFilter === 'all') return true;
    return String(d.tier) === String(tierFilter);
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'present':
      case 'match':
        return {
          icon: <CheckCircle2 className="w-3 h-3 text-emerald-600" />,
          label: t('rep.directories.status_present'),
          className: 'bg-emerald-50 text-emerald-800 border-emerald-200',
        };
      case 'mismatch':
        return {
          icon: <AlertCircle className="w-3 h-3 text-amber-600" />,
          label: t('rep.directories.status_mismatch'),
          className: 'bg-amber-50 text-amber-800 border-amber-200',
        };
      case 'absent':
        return {
          icon: <XCircle className="w-3 h-3 text-rose-600" />,
          label: t('rep.directories.status_absent'),
          className: 'bg-rose-50 text-rose-800 border-rose-200',
        };
      case 'closed':
        return {
          icon: <XCircle className="w-3 h-3 text-slate-400" />,
          label: t('rep.directories.status_closed') || 'Закрыт',
          className: 'bg-slate-100 text-slate-500 border-slate-200',
        };
      case 'not_measured':
      default:
        return {
          icon: <HelpCircle className="w-3 h-3 text-slate-500" />,
          label: t('rep.directories.status_not_measured'),
          className: 'bg-slate-50 text-slate-700 border-slate-200',
        };
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E1D6] rounded-2xl p-8 text-center space-y-3">
        <RefreshCw className="w-6 h-6 animate-spin text-[#C8A653] mx-auto" />
        <div className="text-sm text-[#687077]">{t('rep.citations.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-rose-200 rounded-2xl p-6 text-rose-800">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <AlertCircle className="w-4 h-4 text-rose-600" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#20231F] text-white px-4 py-2.5 rounded-xl shadow-lg text-xs font-medium flex items-center gap-2">
          <Check className="w-4 h-4 text-emerald-400" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Header & Tier Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white border border-[#E5E1D6] rounded-2xl p-4">
        <div>
          <h2 className="text-base font-semibold text-[#20231F] flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#C8A653]" />
            <span>{t('rep.directories.title')}</span>
          </h2>
          <p className="text-xs text-[#687077] mt-0.5">{t('rep.directories.subtitle')}</p>
        </div>

        {/* Tier filter tabs */}
        <div className="flex items-center gap-1.5 p-1 bg-[#F8F7F2] rounded-xl border border-[#E5E1D6] text-xs">
          <button
            onClick={() => setTierFilter('all')}
            className={`px-3 py-1 rounded-lg font-medium transition-colors ${
              tierFilter === 'all' ? 'bg-white text-[#20231F] shadow-2xs font-semibold' : 'text-[#687077] hover:text-[#20231F]'
            }`}
          >
            {t('rep.directories.filter_all')}
          </button>
          <button
            onClick={() => setTierFilter('1')}
            className={`px-3 py-1 rounded-lg font-medium transition-colors ${
              tierFilter === '1' ? 'bg-white text-[#20231F] shadow-2xs font-semibold' : 'text-[#687077] hover:text-[#20231F]'
            }`}
          >
            {t('rep.directories.filter_level1')}
          </button>
          <button
            onClick={() => setTierFilter('2')}
            className={`px-3 py-1 rounded-lg font-medium transition-colors ${
              tierFilter === '2' ? 'bg-white text-[#20231F] shadow-2xs font-semibold' : 'text-[#687077] hover:text-[#20231F]'
            }`}
          >
            {t('rep.directories.filter_level2')}
          </button>
          <button
            onClick={() => setTierFilter('3')}
            className={`px-3 py-1 rounded-lg font-medium transition-colors ${
              tierFilter === '3' ? 'bg-white text-[#20231F] shadow-2xs font-semibold' : 'text-[#687077] hover:text-[#20231F]'
            }`}
          >
            {t('rep.directories.filter_level3')}
          </button>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white border border-[#E5E1D6] rounded-2xl shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-[#E5E1D6] bg-[#F8F7F2] text-[#687077] text-[11px] font-semibold uppercase tracking-wider">
                <th className="py-3 px-3.5">{t('rep.directories.col_name')}</th>
                <th className="py-3 px-3.5">{t('rep.directories.col_level')}</th>
                <th className="py-3 px-3.5">{t('rep.directories.col_cost')}</th>
                <th className="py-3 px-3.5">{t('rep.directories.col_coral')}</th>
                <th className="py-3 px-3.5">{t('rep.directories.col_hallandale')}</th>
                <th className="py-3 px-3.5">{t('rep.directories.col_next_action')}</th>
                <th className="py-3 px-3.5 text-right">{t('rep.directories.col_packet')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1EEE4]">
              {filteredDirectories.map((dir) => {
                const isClosed = dir.status === 'closed';
                const obsCoral = dir.observations?.['coral-springs'] || {};
                const obsHall = dir.observations?.['hallandale-beach'] || {};
                const badgeCoral = isClosed ? getStatusBadge('closed') : getStatusBadge(obsCoral.status);
                const badgeHall = isClosed ? getStatusBadge('closed') : getStatusBadge(obsHall.status);

                return (
                  <tr key={dir.key} className="hover:bg-[#FAF9F5] transition-colors">
                    {/* Platform name & submit link */}
                    <td className="py-3 px-3.5">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-[#20231F]">{dir.name}</span>
                        {!isClosed && dir.submit_url && (
                          <a
                            href={dir.submit_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#C8A653] hover:text-[#A68838]"
                            title="Submit / Claim page"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                      {dir.approx_da && (
                        <div className="text-[10px] text-[#687077] mt-0.5">DA ~{dir.approx_da}</div>
                      )}
                    </td>

                    {/* Level */}
                    <td className="py-3 px-3.5">
                      <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold bg-[#F5EED8] text-[#8C6B1B] border border-[#E8DCB8]">
                        T{dir.tier}
                      </span>
                    </td>

                    {/* Cost */}
                    <td className="py-3 px-3.5">
                      <span className="text-[11px] text-[#20231F]">
                        {dir.cost_type === 'paid' ? t('rep.directories.col_paid') : t('rep.directories.col_standard')}
                      </span>
                    </td>

                    {/* Coral Springs observation */}
                    <td className="py-3 px-3.5">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${badgeCoral.className}`}
                          >
                            {badgeCoral.icon}
                            <span>{badgeCoral.label}</span>
                          </span>
                          {obsCoral.profile_url && (
                            <a
                              href={obsCoral.profile_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#687077] hover:text-[#20231F]"
                            >
                              <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          )}
                        </div>
                        {obsCoral.observed_at && (
                          <div className="text-[10px] text-[#687077]">
                            {obsCoral.observed_at.slice(0, 10)} ({obsCoral.observed_by || 'auto'})
                          </div>
                        )}
                        {obsCoral.profile_url && (
                          <button
                            onClick={() => runDiscrepancyCheck(dir, 'coral-springs')}
                            disabled={checkingKey === `${dir.key}-coral-springs`}
                            className="text-[10px] text-[#C8A653] hover:underline cursor-pointer disabled:opacity-50"
                          >
                            {checkingKey === `${dir.key}-coral-springs`
                              ? t('rep.directories.checking')
                              : t('rep.directories.btn_run_check')}
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Hallandale observation */}
                    <td className="py-3 px-3.5">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${badgeHall.className}`}
                          >
                            {badgeHall.icon}
                            <span>{badgeHall.label}</span>
                          </span>
                          {obsHall.profile_url && (
                            <a
                              href={obsHall.profile_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#687077] hover:text-[#20231F]"
                            >
                              <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          )}
                        </div>
                        {obsHall.observed_at && (
                          <div className="text-[10px] text-[#687077]">
                            {obsHall.observed_at.slice(0, 10)} ({obsHall.observed_by || 'auto'})
                          </div>
                        )}
                        {obsHall.profile_url && (
                          <button
                            onClick={() => runDiscrepancyCheck(dir, 'hallandale-beach')}
                            disabled={checkingKey === `${dir.key}-hallandale-beach`}
                            className="text-[10px] text-[#C8A653] hover:underline cursor-pointer disabled:opacity-50"
                          >
                            {checkingKey === `${dir.key}-hallandale-beach`
                              ? t('rep.directories.checking')
                              : t('rep.directories.btn_run_check')}
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Next action */}
                    <td className="py-3 px-3.5 max-w-[200px]">
                      <span className="text-[11px] text-[#687077] leading-tight block">
                        {isClosed ? (dir.notes || 'Площадка закрыта, не действует') : (obsCoral.next_action || obsHall.next_action || '—')}
                      </span>
                    </td>

                    {/* Action button: Copy Packet */}
                    <td className="py-3 px-3.5 text-right">
                      {isClosed ? (
                        <span className="inline-block px-2.5 py-1 text-xs text-[#8C939D] font-medium bg-[#F5F4F0] rounded-lg border border-[#E5E1D6]">
                          {t('rep.directories.status_closed') || 'Закрыт'}
                        </span>
                      ) : (
                        <button
                          onClick={() => openPacketModal(dir, 'coral-springs')}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] transition-colors cursor-pointer"
                        >
                          <Copy className="w-3 h-3 text-[#C8A653]" />
                          <span>{t('rep.directories.btn_copy_packet')}</span>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Copy Packet Modal */}
      {activePacketDir && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-white border border-[#E5E1D6] rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="p-4 border-b border-[#E5E1D6] flex items-center justify-between bg-[#F8F7F2]">
              <div>
                <div className="text-xs text-[#687077] uppercase tracking-wider font-semibold">
                  {t('rep.directories.packet_title', { name: activePacketDir.name })}
                </div>
                <div className="text-sm font-semibold text-[#20231F] flex items-center gap-2 mt-0.5">
                  <MapPin className="w-3.5 h-3.5 text-[#C8A653]" />
                  <span>{t('rep.directories.office_canon')}</span>
                  <div className="inline-flex rounded-lg border border-[#E5E1D6] bg-white p-0.5 ml-1">
                    <button
                      onClick={() => switchOfficeInModal('coral-springs')}
                      className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                        activeOffice === 'coral-springs' ? 'bg-[#20231F] text-white' : 'text-[#687077]'
                      }`}
                    >
                      {t('rep.directories.col_coral')}
                    </button>
                    <button
                      onClick={() => switchOfficeInModal('hallandale-beach')}
                      className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                        activeOffice === 'hallandale-beach' ? 'bg-[#20231F] text-white' : 'text-[#687077]'
                      }`}
                    >
                      {t('rep.directories.col_hallandale')}
                    </button>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setActivePacketDir(null)}
                className="p-1 rounded-lg text-[#687077] hover:bg-[#E5E1D6] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body: Fields List */}
            <div className="p-4 overflow-y-auto space-y-3 flex-1 text-xs">
              {packetLoading ? (
                <div className="py-8 text-center text-[#687077]">
                  <RefreshCw className="w-5 h-5 animate-spin mx-auto text-[#C8A653]" />
                  <div className="mt-2">{t('rep.directories.loading_canon')}</div>
                </div>
              ) : packetData ? (
                Object.entries(packetData).map(([key, val]) => (
                  <div
                    key={key}
                    className="p-2.5 rounded-xl border border-[#F1EEE4] bg-[#FAF9F5] flex items-start justify-between gap-3"
                  >
                    <div className="flex-1">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8C7A5B]">
                        {key.replace(/_/g, ' ')}
                      </div>
                      <div className="text-xs text-[#20231F] mt-0.5 whitespace-pre-wrap font-sans leading-relaxed">
                        {val || '—'}
                      </div>
                    </div>
                    <button
                      onClick={() => copyToClipboard(val, key)}
                      className="shrink-0 p-1.5 rounded-lg border border-[#E5E1D6] bg-white hover:bg-[#F1EEE4] text-[#20231F] transition-colors"
                      title={t('rep.directories.copy_field')}
                    >
                      {copiedKey === key ? (
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="w-3.5 h-3.5 text-[#687077]" />
                      )}
                    </button>
                  </div>
                ))
              ) : (
                <div className="py-4 text-center text-[#687077]">{t('rep.directories.no_packet_data')}</div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-3 border-t border-[#E5E1D6] bg-[#F8F7F2] flex items-center justify-between">
              <span className="text-[11px] text-[#687077]">
                {t('rep.directories.packet_notice')}
              </span>
              <button
                onClick={copyEntirePacket}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] transition-colors"
              >
                {copiedKey === 'ALL' ? (
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                ) : (
                  <Copy className="w-3.5 h-3.5 text-[#C8A653]" />
                )}
                <span>{t('rep.directories.copy_entire_packet')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectoriesTable;
